<template>
  <v-layout row justify-center>
    <v-dialog v-model="params.show" max-width="600px" persistent>
      <v-form id="newPixPaymentForm">
        <v-card>
          <v-card-title>
            <PaymentIcon name="pix" size="24" class="mr-2" />
            <span class="headline">Novo Pagamento PIX</span>
          </v-card-title>
          <v-card-text>
            <div class="mb-3">
              <h4>Escolha o tipo de pagamento PIX</h4>
            </div>
            <v-container grid-list-md>
              <v-layout wrap>
                <!-- Tipo de PIX -->
                <v-flex xs12>
                  <v-radio-group v-model="params.pix_payment.pix_type" row>
                    <v-radio label="Chave PIX" value="CHAVE_PIX"></v-radio>
                    <v-radio label="PIX Copia e Cola" value="PIX_COPIA_E_COLA"></v-radio>
                    <v-radio label="Dados Bancários" value="DADOS_BANCARIOS"></v-radio>
                  </v-radio-group>
                </v-flex>

                <!-- Campos para Chave PIX -->
                <template v-if="params.pix_payment.pix_type === 'CHAVE_PIX'">
                  <v-flex xs12>
                    <v-text-field
                      label="Chave PIX*"
                      v-model="params.pix_payment.pix_key"
                      persistent-hint
                      hint="CPF, CNPJ, Email, Telefone ou Chave Aleatória"
                      required
                      :rules="[params.rules.required, params.rules.pixKey]"
                      :loading="lookingUpKey"
                      @blur="lookupPixKeyData"
                      @keyup.enter="lookupPixKeyData"
                    >
                      <template v-slot:append>
                        <v-btn 
                          icon 
                          small 
                          @click="lookupPixKeyData"
                          :loading="lookingUpKey"
                          :disabled="!params.pix_payment.pix_key"
                        >
                          <v-icon>search</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-flex>
                  <!-- Informações encontradas da chave PIX -->
                  <v-flex xs12 v-if="pixKeyData.found">
                    <v-alert type="success" outline>
                      <strong>Dados encontrados:</strong><br>
                      <strong>Titular:</strong> {{ pixKeyData.recipient_name }}<br>
                      <strong>Documento:</strong> {{ formatDocument(pixKeyData.recipient_document) }}<br>
                      <span v-if="pixKeyData.bank_name"><strong>Banco:</strong> {{ pixKeyData.bank_name }}</span>
                    </v-alert>
                  </v-flex>
                  <v-flex xs12 v-if="pixKeyData.error">
                    <v-alert type="warning" outline>
                      {{ pixKeyData.error }}
                    </v-alert>
                  </v-flex>
                </template>

                <!-- Campos para PIX Copia e Cola -->
                <template v-if="params.pix_payment.pix_type === 'PIX_COPIA_E_COLA'">
                  <v-flex xs12>
                    <v-textarea
                      label="Código PIX Copia e Cola*"
                      v-model="params.pix_payment.pix_copy_paste"
                      persistent-hint
                      hint="Cole aqui o código PIX completo"
                      rows="3"
                      required
                      :rules="[params.rules.required]"
                    ></v-textarea>
                  </v-flex>
                </template>

                <!-- Campos para Dados Bancários -->
                <template v-if="params.pix_payment.pix_type === 'DADOS_BANCARIOS'">
                  <v-flex xs12 sm6>
                    <v-text-field
                      label="Banco*"
                      v-model="params.pix_payment.bank_code"
                      persistent-hint
                      hint="Código do banco (ex: 341)"
                      required
                      :rules="[params.rules.required, params.rules.bankCode]"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6>
                    <v-text-field
                      label="Agência*"
                      v-model="params.pix_payment.agency"
                      persistent-hint
                      hint="Número da agência"
                      required
                      :rules="[params.rules.required]"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6>
                    <v-text-field
                      label="Conta*"
                      v-model="params.pix_payment.account"
                      persistent-hint
                      hint="Número da conta com dígito"
                      required
                      :rules="[params.rules.required]"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6>
                    <v-select
                      label="Tipo de Conta*"
                      v-model="params.pix_payment.account_type"
                      :items="accountTypes"
                      required
                      :rules="[params.rules.required]"
                    ></v-select>
                  </v-flex>
                </template>

                <!-- Campos comuns -->
                <v-flex xs12>
                  <v-text-field
                    label="Nome do Destinatário*"
                    v-model="params.pix_payment.recipient_name"
                    required
                    :rules="[params.rules.required, params.rules.nameCounter]"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="CPF/CNPJ do Destinatário*"
                    v-model="params.pix_payment.recipient_document"
                    v-mask="['###.###.###-##', '##.###.###/####-##']"
                    persistent-hint
                    hint="CPF ou CNPJ do destinatário"
                    required
                    :rules="[params.rules.required, params.rules.document]"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Valor*"
                    v-model.lazy="params.pix_payment.amount"
                    v-money="money" 
                    :rules="[params.rules.required, params.rules.amountValidation]"
                    required>
                  </v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                    label="Descrição"
                    v-model="params.pix_payment.description"
                    persistent-hint
                    hint="Descrição opcional do pagamento"
                    :counter="140"
                    :rules="[params.rules.descriptionCounter]"
                  ></v-text-field>
                </v-flex>
                <small>*campos requeridos</small>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" flat @click="closeForm">Fechar</v-btn>
            <v-btn
              color="primary"
              :loading="params.loadingButton"
              @click="submitNewPixPayment"
            >Enviar Pagamento</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
    
    <!-- Dialog de Sucesso -->
    <v-dialog v-model="params.pixDialog" persistent max-width="450">
      <v-card>
        <v-card-title class="headline success--text">
          <v-icon color="success" class="mr-2">check_circle</v-icon>
          Pagamento PIX Enviado
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="body-1 mb-3">
            Seu pagamento PIX foi processado com sucesso!
          </p>
          <div v-if="createdPix && createdPix.protocol" class="grey lighten-4 pa-3 rounded">
            <p class="body-2 mb-1"><strong>Protocolo:</strong> {{ createdPix.protocol }}</p>
            <p class="body-2 mb-1"><strong>Valor:</strong> R$ {{ formatAmount(createdPix.amount) }}</p>
            <p class="body-2"><strong>Destinatário:</strong> {{ createdPix.recipient_name }}</p>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="success" @click="params.pixDialog = false">
            <v-icon left>thumb_up</v-icon>
            Entendi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Dialog de Erro -->
    <v-dialog v-model="params.pixDialogErrors" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">Erro no Pagamento PIX</v-card-title>
        <v-card-text v-if="!erroMessage">
          Erro ao processar seu pagamento PIX. Verifique se:
          <ul class="mt-2">
            <li>O serviço PIX está habilitado para sua conta</li>
            <li>Você possui saldo suficiente</li>
            <li>Não excedeu o limite diário de PIX</li>
            <li>Todos os dados estão corretos</li>
          </ul>
        </v-card-text>
        <v-card-text v-else>
          <p class="error--text">{{ erroMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="red"
            block
            center
            @click="params.pixDialogErrors = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapGetters } from "vuex";
import { EventBus } from "@/services/event-bus";
import { VMoney } from 'v-money';
import { mask } from 'vue-the-mask';
import PaymentIcon from '@/components/PaymentIcon.vue';

export default {
  name: "NewPixPaymentForm",
  created() {
    EventBus.$on("show-new-pix-payment-form", () => {
      this.params.show = true;
    });
    this.$store.dispatch("system/fetchSystemConfiguration");
  },
  computed: {
    ...mapGetters({
      createdPix: "bills/pixPayment",
      pixErrors: "bills/pixPaymentError",
      systemAppName: "system/appName",
      account: "account/account",
    }),
    appName() {
      return this.systemAppName || process.env.VUE_APP_APP_NAME || 'FlowIt';
    },
  },
  watch: {
    createdPix(newValue, oldValue) {
      if (newValue && newValue.id) {
        this.params.pixDialog = true;
        this.params.show = false;
        this.params.loadingButton = false;
        // Atualizar listagem se houver
        if (this.$store.dispatch['bills/loadPixPayments']) {
          this.$store.dispatch('bills/loadPixPayments');
        }
        // Limpar formulário
        this.resetForm();
      }
    },
    pixErrors(newValue, oldValue) {
      if (newValue) {
        this.params.pixDialogErrors = true;
        this.params.show = false;
        this.params.loadingButton = false;
        if (newValue.errors && newValue.errors.length > 0) {
          this.erroMessage = newValue.errors[0] || 'Erro desconhecido';
        } else if (newValue.message) {
          this.erroMessage = newValue.message;
        } else {
          this.erroMessage = 'Erro ao processar pagamento PIX';
        }
      }
    }
  },
  data: () => ({
    erroMessage: false,
    lookingUpKey: false,
    pixKeyData: {
      found: false,
      error: null,
      recipient_name: '',
      recipient_document: '',
      bank_name: ''
    },
    accountTypes: [
      { text: 'Conta Corrente', value: 'CONTA_CORRENTE' },
      { text: 'Conta Poupança', value: 'CONTA_POUPANCA' },
      { text: 'Conta Pagamento', value: 'CONTA_PAGAMENTO' }
    ],
    params: {
      show: false,
      pixDialog: false,
      pixDialogErrors: false,
      loadingButton: false,
      pix_payment: {
        pix_type: 'CHAVE_PIX',
        pix_key: '',
        pix_copy_paste: '',
        bank_code: '',
        agency: '',
        account: '',
        account_type: '',
        recipient_name: '',
        recipient_document: '',
        amount: '',
        description: ''
      },
      rules: {
        required: value => !!value || "Campo obrigatório",
        pixKey: value => {
          if (!value) return true;
          // Validação básica de chave PIX
          const cpfRegex = /^\d{11}$/;
          const cnpjRegex = /^\d{14}$/;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const phoneRegex = /^\+?[1-9]\d{1,14}$/;
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          
          const cleanValue = value.replace(/[^0-9]/g, '');
          
          return cpfRegex.test(cleanValue) || 
                 cnpjRegex.test(cleanValue) || 
                 emailRegex.test(value) || 
                 phoneRegex.test(value) || 
                 uuidRegex.test(value) || 
                 "Chave PIX inválida";
        },
        bankCode: value => {
          if (!value) return true;
          return /^\d{3}$/.test(value) || "Código do banco deve ter 3 dígitos";
        },
        document: value => {
          if (!value) return true;
          const cleanValue = value.replace(/[^0-9]/g, '');
          return (cleanValue.length === 11 || cleanValue.length === 14) || "CPF ou CNPJ inválido";
        },
        nameCounter: value => !value || value.length <= 100 || "Máximo 100 caracteres",
        descriptionCounter: value => !value || value.length <= 140 || "Máximo 140 caracteres",
        amountValidation: value => {
          if (!value) return "Campo obrigatório";
          const numValue = parseFloat(value.toString().replace(/[^\d,]/g, '').replace(',', '.'));
          return numValue > 0 || "Valor deve ser maior que zero";
        },
      },
    },
    money: {
      decimal: ',',
      thousands: '.',
      prefix: 'R$ ',
      precision: 2,
      masked: false
    },
  }),
  components: {
    PaymentIcon
  },
  directives: {money: VMoney, mask},
  methods: {
    submitNewPixPayment() {
      // Validação básica
      if (!this.validateForm()) {
        return;
      }

      // Preparar dados baseado no tipo de PIX
      const pixData = {
        pix_type: this.params.pix_payment.pix_type,
        recipient_name: this.params.pix_payment.recipient_name,
        recipient_document: this.params.pix_payment.recipient_document.replace(/[^0-9]/g, ''),
        amount: parseFloat(this.params.pix_payment.amount.replace(/[^\d]+/g,''))/100,
        description: this.params.pix_payment.description
      };

      // Adicionar campos específicos baseado no tipo
      if (this.params.pix_payment.pix_type === 'CHAVE_PIX') {
        pixData.pix_key = this.params.pix_payment.pix_key;
      } else if (this.params.pix_payment.pix_type === 'PIX_COPIA_E_COLA') {
        pixData.pix_copy_paste = this.params.pix_payment.pix_copy_paste;
      } else if (this.params.pix_payment.pix_type === 'DADOS_BANCARIOS') {
        pixData.bank_code = this.params.pix_payment.bank_code;
        pixData.agency = this.params.pix_payment.agency;
        pixData.account = this.params.pix_payment.account;
        pixData.account_type = this.params.pix_payment.account_type;
      }
      
      this.$store.dispatch("bills/newPixPayment", pixData);
      this.params.loadingButton = true;
    },
    validateForm() {
      const payment = this.params.pix_payment;
      
      // Validações comuns
      if (!payment.recipient_name || !payment.recipient_document || !payment.amount) {
        this.erroMessage = "Por favor, preencha todos os campos obrigatórios";
        this.params.pixDialogErrors = true;
        return false;
      }

      // Validações específicas por tipo
      if (payment.pix_type === 'CHAVE_PIX' && !payment.pix_key) {
        this.erroMessage = "Por favor, informe a chave PIX";
        this.params.pixDialogErrors = true;
        return false;
      }
      
      if (payment.pix_type === 'PIX_COPIA_E_COLA' && !payment.pix_copy_paste) {
        this.erroMessage = "Por favor, cole o código PIX";
        this.params.pixDialogErrors = true;
        return false;
      }
      
      if (payment.pix_type === 'DADOS_BANCARIOS') {
        if (!payment.bank_code || !payment.agency || !payment.account || !payment.account_type) {
          this.erroMessage = "Por favor, preencha todos os dados bancários";
          this.params.pixDialogErrors = true;
          return false;
        }
      }

      return true;
    },
    resetForm() {
      this.params.pix_payment = {
        pix_type: 'CHAVE_PIX',
        pix_key: '',
        pix_copy_paste: '',
        bank_code: '',
        agency: '',
        account: '',
        account_type: '',
        recipient_name: '',
        recipient_document: '',
        amount: '',
        description: ''
      };
      this.clearPixKeyData();
    },
    closeForm() {
      this.params.show = false;
      this.resetForm();
    },
    formatAmount(value) {
      if (!value) return '0,00';
      return parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async lookupPixKeyData() {
      const pixKey = this.params.pix_payment.pix_key;
      
      if (!pixKey || pixKey.length < 8) {
        this.clearPixKeyData();
        return;
      }

      // Verificar se a chave está em formato válido
      if (!this.params.rules.pixKey(pixKey) || this.params.rules.pixKey(pixKey) !== true) {
        this.pixKeyData = {
          found: false,
          error: 'Chave PIX em formato inválido',
          recipient_name: '',
          recipient_document: '',
          bank_name: ''
        };
        return;
      }

      this.lookingUpKey = true;
      this.clearPixKeyData();

      try {
        const result = await this.$store.dispatch('bills/lookupPixKey', pixKey);
        
        if (result.success) {
          this.pixKeyData = {
            found: true,
            error: null,
            recipient_name: result.data.recipient_name,
            recipient_document: result.data.recipient_document,
            bank_name: result.data.bank_name
          };

          // Auto-preencher campos se encontrou dados
          if (result.data.recipient_name) {
            this.params.pix_payment.recipient_name = result.data.recipient_name;
          }
          if (result.data.recipient_document) {
            this.params.pix_payment.recipient_document = this.formatDocument(result.data.recipient_document);
          }
        } else {
          this.pixKeyData = {
            found: false,
            error: result.error || 'Chave PIX não encontrada',
            recipient_name: '',
            recipient_document: '',
            bank_name: ''
          };
        }
      } catch (error) {
        this.pixKeyData = {
          found: false,
          error: 'Erro ao consultar chave PIX',
          recipient_name: '',
          recipient_document: '',
          bank_name: ''
        };
      } finally {
        this.lookingUpKey = false;
      }
    },
    clearPixKeyData() {
      this.pixKeyData = {
        found: false,
        error: null,
        recipient_name: '',
        recipient_document: '',
        bank_name: ''
      };
    },
    formatDocument(document) {
      if (!document) return '';
      
      // Remove todos os caracteres não numéricos
      const cleanDoc = document.replace(/[^0-9]/g, '');
      
      if (cleanDoc.length === 11) {
        // CPF: 000.000.000-00
        return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      } else if (cleanDoc.length === 14) {
        // CNPJ: 00.000.000/0000-00
        return cleanDoc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }
      
      return document; // Retorna original se não for CPF nem CNPJ
    }
  }
};
</script>

<style scoped>
.v-card {
  border-radius: 8px !important;
}

.v-card-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
}

.v-card-title .headline {
  color: white !important;
}

.v-text-field, .v-textarea, .v-select {
  margin-bottom: 8px;
}

.v-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
}

.mb-3 h4 {
  color: #333;
  font-weight: 500;
  margin-bottom: 8px;
}

.rounded {
  border-radius: 8px;
}
</style>